import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { linkifyText } from "../../utils/dom";
import { ChevronDown, ChevronUp } from "../icons/svg/outline";
import { IoAdd, IoTrashOutline } from "react-icons/io5";

const MAX_LINK_DISPLAY_LENGTH = 50;

/**
 * Truncates long link text in HTML string so displayed URLs don't overflow.
 * Preserves full href; only the visible text is shortened with an ellipsis.
 */
function truncateLongLinksInHtml(html, maxLength = MAX_LINK_DISPLAY_LENGTH) {
  if (!html) return html;
  return html.replace(
    /<a\s([^>]*)>([^<]*)<\/a>/g,
    (_, attrs, text) => {
      if (text.length <= maxLength) return `<a ${attrs}>${text}</a>`;
      const truncated = `${text.slice(0, maxLength - 1)}…`;
      return `<a ${attrs}>${truncated}</a>`;
    }
  );
}

/**
 * A component that renders a textarea where URLs are automatically converted to clickable links.
 * Optionally shows a labeled header row with expand/collapse; when expanded, the display area
 * grows to show full content. When a label is provided, the whole header is clickable to toggle.
 *
 * @param {object} props
 * @param {string} props.value - The current value of the textarea
 * @param {function} props.onChange - Function to call when the textarea content changes
 * @param {string} props.placeholder - Placeholder text for the empty textarea
 * @param {string} props.className - CSS classes to apply to the textarea/content area
 * @param {string} [props.label] - Optional label shown in a header row; when set, header includes expand/collapse chevron (hidden while editing)
 * @param {boolean} [props.staticDisplay] - Read-only snapshot: label row + linkified HTML only (no edit/add/delete/expand). For PDF/PNG capture.
 * @returns {JSX.Element}
 */
const LinkifiedTextArea = ({
  readOnly = false,
  staticDisplay = false,
  value,
  onChange,
  placeholder,
  className,
  label,
  onLabelChange,
  onAdd,
  onDelete,
  onBlur,
  onLabelBlur,
}) => {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [labelEditing, setLabelEditing] = useState(false);
  const [linkifiedContent, setLinkifiedContent] = useState("");
  const [hasOverflowWhenCollapsed, setHasOverflowWhenCollapsed] = useState(false);
  const elementRef = useRef(null);
  const nextTextareaHeightRef = useRef(null);
  const labelInputRef = useRef(null);

  // Focus the label input when editing
  useLayoutEffect(() => {
    if (!labelEditing) return;
    labelInputRef.current?.focus();
  }, [labelEditing]);

  // Linkify the content
  useEffect(() => {
    const result = linkifyText(value, true);
    setLinkifiedContent(truncateLongLinksInHtml(result.html));
  }, [value]);

  // When collapsed and not editing, measure if content overflows (vertical or horizontal); hide chevron when there's nothing to expand
  useLayoutEffect(() => {
    if (staticDisplay || editing || expanded) return;
    const el = elementRef.current;
    if (!el) return;
    const hasOverflow =
      el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
    setHasOverflowWhenCollapsed(hasOverflow);
  }, [staticDisplay, editing, expanded, linkifiedContent]);

  const handleBlur = () => {
    setEditing(false);
    onBlur?.();
  };

  const handleFocus = () => {
    const el = elementRef.current;
    if (el && typeof el.offsetHeight === "number") {
      nextTextareaHeightRef.current = el.offsetHeight;
    }
    setEditing(true);
  };

  const handleContainerClick = (e) => {
    if (e.target.closest("a")) return;
    handleFocus();
  };

  const showExpandCollapse =
    Boolean(label) && !editing && (expanded || hasOverflowWhenCollapsed);

  if (staticDisplay) {
    return (
      <div className="flex flex-col w-full h-full">
        <div className="text-gray-800 mx-2 border-b border-solid border-gray-300 flex flex-row items-center justify-between gap-2 min-h-[28px]">
          <div className="min-w-0 flex-1">
            <span className="block text-left font-medium text-gray-800 min-w-0 w-full">
              {label}
            </span>
          </div>
        </div>
        <div className="flex flex-row rounded text-gray-800 pb-2 min-h-24 h-full overflow-hidden">
          <div
            className={`${className} !resize-none overflow-visible mb-[7px] min-h-[108px] break-words`}
          >
            {linkifiedContent ? (
              <div dangerouslySetInnerHTML={{ __html: linkifiedContent }} />
            ) : (
              <div className="text-gray-400">{placeholder}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full">
      <div className="text-gray-800 mx-2 border-b border-solid border-gray-300 flex flex-row items-center justify-between gap-2 min-h-[28px]">
        <div className="min-w-0 flex-1">
          {labelEditing ? (
            <input
              ref={labelInputRef}
              type="text"
              value={label}
              onChange={(e) => onLabelChange(e.target.value)}
              onBlur={() => {
                setLabelEditing(false);
                onLabelBlur?.();
              }}
              className="font-medium text-gray-800 caret-black bg-transparent rounded p-0 min-w-0 w-full max-w-full outline-none focus:ring-0 focus:ring-gray-400 focus:ring-offset-0"
            />
          ) : (
            <button
              type="button"
              className="text-left font-medium text-gray-800 min-w-0 w-full cursor-text"
              onClick={() => setLabelEditing(true)}
            >
              {label}
            </button>
          )
          }
        </div>
        <div className="flex flex-row items-center shrink-0 gap-0.5">
          {showExpandCollapse && (
            <button
              type="button"
              className="p-0.5 text-gray-600 hover:text-gray-900 cursor-pointer select-none rounded"
              aria-label={expanded ? "Collapse" : "Expand"}
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? (
                <ChevronUp className="w-4 h-4 shrink-0" />
              ) : (
                <ChevronDown className="w-4 h-4 shrink-0" />
              )}
            </button>
          )}
          {onAdd && (
            <button
              type="button"
              className="p-0.5 text-gray-600 hover:text-gray-900"
              aria-label="Add extra data section"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAdd();
              }}
            >
              <IoAdd size={20} />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className="p-0.5 text-gray-600 hover:text-red-600"
              aria-label="Remove extra data section"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete();
              }}
            >
              <IoTrashOutline size={18} />
            </button>
          )}
        </div>
      </div>
      <div className={`flex flex-row rounded text-gray-800 pb-2 min-h-24 h-full overflow-hidden`}>
        {editing ? (
          <textarea
            ref={elementRef}
            value={value}
            onChange={onChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            className={className}
            style={{
              resize: "vertical",
              minHeight: `${Math.max(108, nextTextareaHeightRef.current ?? 108)}px`,
            }}
            autoFocus
          />
        ) : (
          <div
            ref={elementRef}
            className={`${className} !resize-none overflow-x-hidden overflow-y-auto mb-[7px] min-h-[108px] break-words ${readOnly || expanded ? "" : "max-h-[108px]"}`}
            onClick={handleContainerClick}
          >
            {linkifiedContent ? (
              <div dangerouslySetInnerHTML={{ __html: linkifiedContent }} />
            ) : (
              <div className="text-gray-400">{placeholder}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkifiedTextArea;
